import { BillingEntityTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import Text from '@app/components/ui/Text/Text';
import { getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import type { TranslationKeys } from '@app/i18n/translationKeys';
import { APP_PREFIX, CUSTOMER_PROFILE_PAGE, PAYMENTS_PAGE } from '@app/routes/urls';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { useTableEmptyState } from '@hooks/useTableEmptyState';
import { currencyFormatter, type SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import type { TableColumn } from '@konnecta/generic-table';
import { GenericTable, TableSearchInput } from '@konnecta/generic-table';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconBaseProps, IconType } from 'react-icons';
import { FiExternalLink } from 'react-icons/fi';
import { LuStethoscope } from 'react-icons/lu';
import { MdOutlineTipsAndUpdates } from 'react-icons/md';
import { RiNurseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_BALANCES_SORT, type BalanceRow, type BalanceTabKey } from './types';
import { useBalances } from './useBalances';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

type BalanceTab = {
  key: BalanceTabKey;
  labelKey: TranslationKeys;
  summaryKey: TranslationKeys;
  infoKey: TranslationKeys;
  total: number;
  color: string;
  icon: IconType;
};

const EuroGlyphIcon: IconType = ({ size = 12 }: IconBaseProps) => (
  <span style={{ fontSize: size, lineHeight: 1, fontWeight: 600 }} aria-hidden>
    €
  </span>
);

const BALANCE_TAB_BASE_CONFIG: Omit<BalanceTab, 'total'>[] = [
  {
    key: BillingEntityTypeEnum.Client,
    labelKey: 'clients',
    summaryKey: 'total-balance-clients',
    infoKey: 'balances-info-clients',
    color: '#16a34a',
    icon: EuroGlyphIcon,
  },
  {
    key: BillingEntityTypeEnum.Doctor,
    labelKey: 'doctors',
    summaryKey: 'total-balance-doctors',
    infoKey: 'balances-info-partners',
    color: '#94a3b8',
    icon: LuStethoscope,
  },
  {
    key: BillingEntityTypeEnum.Caregiver,
    labelKey: 'caregivers',
    summaryKey: 'total-balance-caregivers',
    infoKey: 'balances-info-partners',
    color: '#7dd3fc',
    icon: RiNurseFill,
  },
];

const getBalanceColor = (value: number, entityType: BalanceTabKey): string => {
  if (value === 0) {
    return '#64748b';
  }

  if (entityType === BillingEntityTypeEnum.Client) {
    return value > 0 ? '#16a34a' : '#be123c';
  }

  return value > 0 ? '#16a34a' : '#be123c';
};

const formatSignedBalance = (value: number, currency: SupportedCurrencies, locale: string) => {
  if (value === 0) {
    return currencyFormatter(0, currency, locale);
  }

  const sign = value > 0 ? '+' : '-';
  return `${sign}${currencyFormatter(Math.abs(value), currency, locale)}`;
};

const getSignLegend = (entityType: BalanceTabKey, t: (key: TranslationKeys) => string) =>
  entityType === BillingEntityTypeEnum.Client
    ? [
        { sign: '+', color: '#16a34a', text: t('balances-sign-guide-clients-positive') },
        { sign: '-', color: '#be123c', text: t('balances-sign-guide-clients-negative') },
      ]
    : [
        { sign: '+', color: '#16a34a', text: t('balances-sign-guide-partners-positive') },
        { sign: '-', color: '#be123c', text: t('balances-sign-guide-partners-negative') },
      ];

const Balances = () => {
  const { i18n, t } = useTranslation();
  const tableEmptyState = useTableEmptyState<BalanceRow>();
  const navigate = useNavigate();
  const locale = getNumberFormattingLocaleByLanguage(i18n.language);

  const {
    activeTab,
    searchTerm,
    sort,
    rows,
    totalCount,
    totalsByEntityType,
    currency,
    isListLoading,
    isListFetching,
    setActiveTab,
    setSearchTerm,
    setSort,
    setCurrentPage,
    setPageSize,
  } = useBalances();

  const tabs = useMemo<BalanceTab[]>(
    () =>
      BALANCE_TAB_BASE_CONFIG.map((tab) => ({
        ...tab,
        total: totalsByEntityType[tab.key] ?? 0,
      })),
    [totalsByEntityType]
  );

  const activeMeta = useMemo(() => tabs.find((tab) => tab.key === activeTab) ?? tabs[0], [activeTab, tabs]);

  const activeTotalColor = getBalanceColor(activeMeta.total, activeMeta.key);
  const signLegend = getSignLegend(activeMeta.key, t);

  const columns = useMemo(
    (): TableColumn<BalanceRow>[] => [
      {
        id: 'name',
        header: t('name-capital'),
        accessor: 'name',
        width: 260,
        sortable: true,
        sortField: 'name',
        copy: false,
        cellRenderer: (value) => {
          const name = value as string;
          const initials = getInitials(name, { fallback: '' });
          return (
            <div className="flex items-center gap-3">
              <AppAvatar isSelected={true} fullName={name} value={initials} />
              <Text type="body2" untranslatedText={name} />
            </div>
          );
        },
      },
      {
        id: 'transaction',
        header: t('last-movement-capital'),
        accessor: 'transaction',
        width: 260,
        sortable: true,
        sortField: 'transaction',
        copy: false,
        cellRenderer: (value) => <Text type="body2" untranslatedText={value as string} color="#64748b" />,
      },
      {
        id: 'balance',
        header: t('balance-capital'),
        accessor: 'balance',
        align: 'right',
        width: 260,
        sortable: true,
        sortField: 'balance',
        copy: false,
        cellRenderer: (value) => (
          <div className="flex w-full justify-end">
            <Text
              type="body2"
              untranslatedText={formatSignedBalance(value as number, currency, locale)}
              color={getBalanceColor(value as number, activeTab)}
              fw={600}
              noWrap
              className="tabular-nums"
            />
          </div>
        ),
      },
      createdAtColumn<BalanceRow>(t),
    ],
    [t, currency, locale, activeTab]
  );

  return (
    <div className="h-[calc(100vh-100px)] overflow-hidden">
      <div className="h-full w-full bg-slate-50 flex flex-col">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-baseline gap-2">
              <Text type="h4" untranslatedText={`${t(activeMeta.summaryKey)}:`} />

              <Text
                type="h3"
                untranslatedText={formatSignedBalance(activeMeta.total, currency, locale)}
                color={activeTotalColor}
                fw={700}
              />

              <Tooltip
                placement="top"
                title={
                  <div className="flex flex-col gap-3">
                    <Text type="body2" transKey={activeMeta.infoKey} color="#0f172a" lh={1.45} />
                    <div className="flex flex-col gap-2.5">
                      {signLegend.map((item) => (
                        <div key={item.sign} className="flex items-center gap-3">
                          <span
                            className="inline-flex h-7 min-w-7 items-center justify-center rounded-md border bg-white px-2 text-sm font-semibold"
                            style={{ borderColor: item.color, color: item.color }}
                          >
                            {item.sign}
                          </span>
                          <Text type="body2" untranslatedText={item.text} color="#0f172a" className="leading-6" />
                        </div>
                      ))}
                    </div>
                  </div>
                }
                styles={{
                  root: { maxWidth: 500 },
                  body: {
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    color: '#0f172a',
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
                    padding: '12px 14px',
                  },
                }}
              >
                <button
                  type="button"
                  aria-label={t('balances-tip-aria')}
                  className="inline-flex items-center gap-2 text-slate-500 transition-colors hover:text-slate-700"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-500">
                    <MdOutlineTipsAndUpdates size={15} />
                  </span>
                  <span className="text-xs font-semibold tracking-wide">TIP</span>
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <TableSearchInput
              key={activeTab}
              placeholder={t('search')}
              defaultValue={searchTerm}
              onSearch={setSearchTerm}
              className="w-56"
            />

            <div className="h-6 w-px bg-slate-200" />

            {tabs.map((tab) => {
              const isActive = tab.key === activeTab;
              const Icon = tab.icon;
              const iconColor = tab.color;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                    isActive ? 'text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                  style={{
                    backgroundColor: isActive ? iconColor : '#ffffff',
                    borderColor: isActive ? iconColor : '#e2e8f0',
                  }}
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full border"
                    style={{
                      backgroundColor: isActive ? '#ffffff' : iconColor,
                      borderColor: isActive ? '#ffffff' : iconColor,
                      color: isActive ? iconColor : '#ffffff',
                    }}
                  >
                    <Icon size={12} />
                  </span>

                  <Text
                    type="label2"
                    transKey={tab.labelKey}
                    className={isActive ? 'text-white' : ''}
                    color={isActive ? '#ffffff' : undefined}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <GenericTable<BalanceRow>
            tableId="balances"
            emptyState={tableEmptyState}
            data={rows}
            columns={columns}
            loading={isListLoading}
            isFetching={isListFetching}
            totalCount={totalCount}
            serverSide={true}
            rowKey="id"
            initialState={{
              sortColumns: [
                {
                  columnId: 'transaction',
                  direction: 'desc',
                },
              ],
            }}
            urlParamConfig={{ page: 'page', pageSize: 'page_size' }}
            features={{ filtering: false, sorting: true, columnVisibility: false, columnReordering: false }}
            rowActions={{
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              getActions: (row) => [
                {
                  key: 'profile',
                  label: (
                    <div className="flex items-center gap-3 px-3 py-2">
                      <FiExternalLink size={18} className="text-slate-500" />
                      <Text type="body2" transKey="ledgers" />
                    </div>
                  ),
                  onClick: (r) =>
                    navigate(`/${APP_PREFIX}/${CUSTOMER_PROFILE_PAGE}?id=${r.entityId}&type=${r.entityType}`),
                },
                {
                  key: 'payment',
                  label: (
                    <div className="flex items-center gap-3 px-3 py-2">
                      <span className="text-[18px] leading-none text-emerald-500">€</span>
                      <Text type="body2" transKey="add-payment" />
                    </div>
                  ),
                  onClick: (r) => navigate(`/${APP_PREFIX}/${PAYMENTS_PAGE}?id=${r.entityId}&type=${r.entityType}`),
                },
              ],
            }}
            onParamsChange={(params) => {
              const nextSort =
                params.sorting && params.sorting.length > 0
                  ? params.sorting
                      .map((sortColumn) => {
                        const matchedColumn = columns.find((column) => column.id === sortColumn.field);
                        const resolvedField = matchedColumn?.sortField ?? sortColumn.field;
                        const sortPrefix = sortColumn.direction === 'desc' ? '-' : '';
                        return `${sortPrefix}${resolvedField}`;
                      })
                      .join(',')
                  : DEFAULT_BALANCES_SORT;

              if (nextSort !== sort) {
                setSort(nextSort);
              }

              setCurrentPage(params.page + 1);
              setPageSize(params.pageSize);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Balances;
